// NDLCS Legal Writing Academy: enrolment, drafting exercises, AI grading, admin sign-off.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

const GRADE_MODEL = "google/gemini-2.5-flash";

// ---------- Helpers ----------

async function publicClient() {
  const { createClient } = await import("@supabase/supabase-js");
  return createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
}

async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error || !data) throw new Error("Forbidden");
}

function countWords(s: string) {
  return s.trim().split(/\s+/).filter(Boolean).length;
}

// ---------- Public ----------

export const listWritingCourses = createServerFn({ method: "GET" }).handler(async () => {
  const sb = await publicClient();
  const { data, error } = await sb
    .from("writing_courses")
    .select("slug, code, title, summary, hero_blurb, level, sort_order")
    .eq("is_published", true)
    .order("sort_order");
  if (error) throw new Error(error.message);
  return { courses: data ?? [] };
});

export const getWritingCoursePublic = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) => z.object({ slug: z.string().min(2).max(80) }).parse(i))
  .handler(async ({ data }) => {
    const sb = await publicClient();
    const { data: course } = await sb
      .from("writing_courses")
      .select("slug, code, title, summary, hero_blurb, level, pass_threshold")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (!course) throw new Error("Course not found");
    const { data: lessons } = await sb
      .from("writing_lessons")
      .select("id, order_index, title, drafting_tips")
      .eq("course_slug", data.slug)
      .order("order_index");
    return { course, lessons: lessons ?? [] };
  });

// ---------- Learner ----------

export const enrolInWritingCourse = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ slug: z.string().min(2).max(80) }).parse(i))
  .handler(async ({ data, context }) => {
    const { data: existing } = await context.supabase
      .from("writing_enrollments")
      .select("id")
      .eq("user_id", context.userId)
      .eq("course_slug", data.slug)
      .maybeSingle();
    if (existing) return { ok: true, wasNew: false };
    const { error } = await context.supabase
      .from("writing_enrollments")
      .insert({ user_id: context.userId, course_slug: data.slug });
    if (error) throw new Error(error.message);
    return { ok: true, wasNew: true };
  });

export const getWritingCourseForLearner = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ slug: z.string().min(2).max(80) }).parse(i))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { data: course } = await sb
      .from("writing_courses")
      .select("slug, code, title, summary, hero_blurb, pass_threshold")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (!course) throw new Error("Course not found");

    const { data: enrolment } = await sb
      .from("writing_enrollments")
      .select("id, status, enrolled_at, completed_at, certificate_id")
      .eq("user_id", context.userId)
      .eq("course_slug", data.slug)
      .maybeSingle();

    const { data: lessons } = await sb
      .from("writing_lessons")
      .select("id, order_index, title, body_md, drafting_tips")
      .eq("course_slug", data.slug)
      .order("order_index");

    const lessonIds = (lessons ?? []).map((l: any) => l.id);
    const { data: exercises } = lessonIds.length
      ? await sb
          .from("writing_exercises")
          .select("id, lesson_id, order_index, prompt, scenario, min_words, max_words, rubric_public")
          .in("lesson_id", lessonIds)
          .order("order_index")
      : { data: [] as any[] };

    const { data: submissions } = await sb
      .from("writing_submissions")
      .select("id, exercise_id, draft_text, word_count, version_no, parent_submission_id, ai_score, ai_feedback, admin_score, admin_feedback, status, submitted_at, graded_at")
      .eq("user_id", context.userId)
      .eq("course_slug", data.slug)
      .order("version_no", { ascending: false });

    return {
      course,
      enrolment: enrolment ?? null,
      lessons: lessons ?? [],
      exercises: exercises ?? [],
      submissions: submissions ?? [],
    };
  });

export const submitWritingExercise = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      exerciseId: z.string().uuid(),
      draftText: z.string().trim().min(20).max(20000),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Caller must be enrolled
    const { data: ex, error: eErr } = await supabaseAdmin
      .from("writing_exercises")
      .select(`
        id, prompt, scenario, rubric, min_words, max_words,
        lesson:writing_lessons!inner(course_slug, exemplar_md, title)
      `)
      .eq("id", data.exerciseId)
      .maybeSingle();
    if (eErr || !ex) throw new Error("Exercise not found");

    const courseSlug = (ex.lesson as any).course_slug as string;
    const { data: enrolment } = await supabaseAdmin
      .from("writing_enrollments")
      .select("id")
      .eq("user_id", context.userId)
      .eq("course_slug", courseSlug)
      .maybeSingle();
    if (!enrolment) throw new Error("Enrol in the course first");

    const words = countWords(data.draftText);
    if (words < ex.min_words) {
      throw new Error(`Draft is too short (min ${ex.min_words} words; you wrote ${words})`);
    }
    if (words > ex.max_words) {
      throw new Error(`Draft is too long (max ${ex.max_words} words; you wrote ${words})`);
    }

    // Academic-integrity check: reject drafts our classifier estimates are
    // more than 20% AI-written. We do this BEFORE grading so we don't burn
    // tokens on a draft we're going to bounce.
    const { assessAiAuthorship, AI_GUARD_DEFAULT_THRESHOLD_PCT, aiGuardRejectionMessage } =
      await import("./ai-content-guard.server");
    const aiCheck = await assessAiAuthorship(data.draftText);
    if (aiCheck.ai_probability_pct > AI_GUARD_DEFAULT_THRESHOLD_PCT) {
      throw new Error(aiGuardRejectionMessage(aiCheck.ai_probability_pct, AI_GUARD_DEFAULT_THRESHOLD_PCT));
    }


    // AI grading
    const rubric = Array.isArray(ex.rubric) ? ex.rubric : [];
    const aiKey = process.env.LOVABLE_API_KEY;
    let aiScore: number | null = null;
    let aiFeedback: any = { error: "AI grading unavailable" };
    let usedModel: string | null = null;

    if (aiKey) {
      const sys =
        "You are a senior South African legal drafting examiner. Grade a learner's draft against the rubric. " +
        "Return STRICT JSON: { score: 0-100, summary: string, strengths: string[], improvements: string[], rubric: [{criterion, weight, awarded, comment}] }. " +
        "Be honest but constructive. Compare against the exemplar but do not require identical wording.";
      const user = JSON.stringify({
        lesson: (ex.lesson as any).title,
        prompt: ex.prompt,
        scenario: ex.scenario,
        rubric,
        exemplar: (ex.lesson as any).exemplar_md,
        learner_draft: data.draftText,
      });
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 45000);
        const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Lovable-API-Key": aiKey },
          body: JSON.stringify({
            model: GRADE_MODEL,
            messages: [
              { role: "system", content: sys },
              { role: "user", content: user },
            ],
            response_format: { type: "json_object" },
          }),
          signal: ctrl.signal,
        }).finally(() => clearTimeout(timer));
        if (res.ok) {
          const j: any = await res.json();
          const text = j.choices?.[0]?.message?.content ?? "";
          const parsed = JSON.parse(text);
          aiScore = Math.max(0, Math.min(100, Number(parsed.score) || 0));
          aiFeedback = parsed;
          usedModel = GRADE_MODEL;
        }
      } catch (err) {
        console.error("[writing-academy] AI grading failed", err);
      }
    }

    const status = aiScore != null ? "ai_graded" : "submitted";

    // Versioning: each submission for the same (user, exercise) increments version_no
    // and links back to the previous attempt as parent_submission_id.
    const { data: prior } = await supabaseAdmin
      .from("writing_submissions")
      .select("id, version_no")
      .eq("user_id", context.userId)
      .eq("exercise_id", data.exerciseId)
      .order("version_no", { ascending: false })
      .limit(1)
      .maybeSingle();

    const nextVersion = (prior?.version_no ?? 0) + 1;

    const { data: ins, error: iErr } = await supabaseAdmin
      .from("writing_submissions")
      .insert({
        user_id: context.userId,
        exercise_id: data.exerciseId,
        course_slug: courseSlug,
        draft_text: data.draftText,
        word_count: words,
        version_no: nextVersion,
        parent_submission_id: prior?.id ?? null,
        ai_score: aiScore,
        ai_feedback: aiFeedback,
        ai_model: usedModel,
        ai_content_check: aiCheck as any,
        ai_content_pct: aiCheck.ai_probability_pct,
        status,
        graded_at: aiScore != null ? new Date().toISOString() : null,
      })
      .select("id, ai_score, ai_feedback, status, word_count, version_no, ai_content_pct")
      .single();
    if (iErr) throw new Error(iErr.message);
    return ins;
  });

export const listSubmissionVersions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ exerciseId: z.string().uuid() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("writing_submissions")
      .select("id, version_no, draft_text, word_count, ai_score, ai_feedback, admin_score, admin_feedback, status, submitted_at, graded_at")
      .eq("user_id", context.userId)
      .eq("exercise_id", data.exerciseId)
      .order("version_no", { ascending: false });
    if (error) throw new Error(error.message);
    return { versions: rows ?? [] };
  });

// ---------- Admin ----------

export const adminListWritingSubmissions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("writing_submissions")
      .select(`
        id, user_id, course_slug, draft_text, word_count, ai_score, ai_feedback,
        admin_score, admin_feedback, status, submitted_at, graded_at,
        exercise:writing_exercises(prompt, lesson:writing_lessons(title, course_slug))
      `)
      .order("submitted_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { submissions: data ?? [] };
  });

export const adminGradeWritingSubmission = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      submissionId: z.string().uuid(),
      score: z.number().int().min(0).max(100),
      feedback: z.string().trim().min(3).max(4000),
      decision: z.enum(["pass", "revise"]),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("writing_submissions")
      .update({
        admin_score: data.score,
        admin_feedback: data.feedback,
        status: data.decision === "pass" ? "passed" : "revise",
        graded_at: new Date().toISOString(),
      })
      .eq("id", data.submissionId);
    if (error) throw new Error(error.message);

    // If pass and all exercises in the course are passed → issue certificate.
    if (data.decision === "pass") {
      await maybeIssueWritingCertificate(supabaseAdmin, data.submissionId, context.userId);
    }
    return { ok: true };
  });

async function maybeIssueWritingCertificate(sb: any, submissionId: string, adminId: string) {
  const { data: sub } = await sb
    .from("writing_submissions")
    .select("user_id, course_slug")
    .eq("id", submissionId)
    .maybeSingle();
  if (!sub) return;

  // All exercises in this course
  const { data: lessons } = await sb
    .from("writing_lessons")
    .select("id")
    .eq("course_slug", sub.course_slug);
  const lessonIds = (lessons ?? []).map((l: any) => l.id);
  if (!lessonIds.length) return;
  const { data: exs } = await sb
    .from("writing_exercises")
    .select("id")
    .in("lesson_id", lessonIds);
  const exerciseIds = (exs ?? []).map((e: any) => e.id);
  if (!exerciseIds.length) return;

  // Best status per exercise for this learner
  const { data: subs } = await sb
    .from("writing_submissions")
    .select("exercise_id, status, admin_score, ai_score")
    .eq("user_id", sub.user_id)
    .in("exercise_id", exerciseIds);

  const passedExercises = new Set(
    (subs ?? [])
      .filter((s: any) => s.status === "passed" || (s.admin_score ?? 0) >= 70)
      .map((s: any) => s.exercise_id),
  );
  if (passedExercises.size < exerciseIds.length) return;

  // Already certified?
  const { data: enrolment } = await sb
    .from("writing_enrollments")
    .select("id, certificate_id")
    .eq("user_id", sub.user_id)
    .eq("course_slug", sub.course_slug)
    .maybeSingle();
  if (!enrolment || enrolment.certificate_id) return;

  const { data: course } = await sb
    .from("writing_courses")
    .select("code, title")
    .eq("slug", sub.course_slug)
    .maybeSingle();
  const { data: profile } = await sb
    .from("profiles")
    .select("full_name, email")
    .eq("id", sub.user_id)
    .maybeSingle();
  if (!course || !profile) return;

  const { data: rows, error: iErr } = await sb.rpc("issue_certificate", {
    _user_id: sub.user_id,
    _track: "tutoring",
    _track_ref: course.code,
    _title: `${course.code} — ${course.title}`,
    _recipient_name: profile.full_name || profile.email || "Learner",
    _metadata: {
      writing_academy: true,
      course_slug: sub.course_slug,
      approved_by: adminId,
    },
  });
  if (iErr) {
    console.error("[writing-academy] certificate issuance failed", iErr);
    return;
  }
  const row = Array.isArray(rows) ? rows[0] : rows;
  await sb
    .from("writing_enrollments")
    .update({
      status: "certified",
      completed_at: new Date().toISOString(),
      certificate_id: row.id,
    })
    .eq("id", enrolment.id);
}
